import styles from './ExamChangeInfoSharedStyling.module.scss'
import React, { ReactElement } from 'react'
import { ExamChangeInfoSharedProps } from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedProps.types'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Spring2024: React.FC<ExamChangeInfoSharedProps> = (
    _props
): ReactElement => {
    return (
        <div
            className={styles.examChangeGuideSharedComponent}
            id={'spring2024'}
        >
            <div className={styles.examChangeGuidePageTop}>
                <h2>Spring 2024</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has released changes effective for the Spring 2024
                    sitting of FSA Group and Health Exams. The updated syllabi
                    can be found here:
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/4ab3c7/globalassets/assets/files/edu/2024/spring/syllabi/ghdp.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Design and Pricing
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/4ab3d0/globalassets/assets/files/edu/2024/spring/syllabi/ghvru.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Valuation and Regulation
                            </a>{' '}
                            (Unchanged from Fall 2023)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/4ab3ca/globalassets/assets/files/edu/2024/spring/syllabi/ghrm.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Risk Mitigation
                            </a>
                        </li>
                    </ul>
                    The overall trend we noticed from the changes is that the
                    syllabi are shrinking in size (more content is being removed
                    than new content is being added).
                    <br />
                    <br />
                    GHDP has the following changes:
                    <ul>
                        <li>
                            <b>Removed</b> - The Essentials of Managed
                            Healthcare textbook was removed entirely. This
                            removed 2 chapters from Plan Provisions and
                            Government Programs, and 1 chapter from Manual
                            Rates. The last time this textbook was tested was
                            the GHFVU Spring 2022 Sitting (Q6 and Q12c), before
                            it was moved to the GHDP exam. Overall, this
                            textbook was not a major part of the syllabus and
                            was not frequently tested.
                        </li>
                        <li>
                            <b>Removed</b> - Pricing Critical Illness Insurance
                            in Canada (a study note from the Manual rates
                            section). This was a fairly small study note, and
                            had only been tested once in the past (GHADV Spring
                            2018 Q3).
                        </li>
                        <li>
                            <b>Added</b> - 1 new website:{' '}
                            <a
                                href={
                                    'https://www.soa.org/sections/health/health-newsletter/2023/september/hw-2023-09-howard/'
                                }
                                className={reusableCssClass.clickableText}
                            >
                                Critical Illness Turns 40!
                            </a>
                            . This website is fairly short.
                        </li>
                        <li>
                            <b>Added</b> - 1 new Excel model (details TBD):
                            Calculated Risk: Driving Decisions Using the 5/50
                            Research. We will update this post as soon as we
                            know more about this item.
                        </li>
                        <li>
                            <b>Updated</b> - The Medicare Part D Prescription
                            Drug Benefits (study note from the Plan Provisions
                            and Government Programs section) was slightly
                            updated.
                        </li>
                        <li>
                            <b>Added</b> - 1 new study note / Excel model
                            (details TBD): Retiree Health Benefits - Accounting
                            and Valuation Assumptions. However,{' '}
                            <b>this will not be tested until Fall 2024</b>.
                        </li>
                    </ul>
                    Note: While it looks like more was removed than added to the
                    syllabus, the items that were removed were not tested
                    frequently.
                    <br />
                    <br />
                    GHRM has the following changes:
                    <ul>
                        <li>
                            <b>Removed</b> - The Handbook of Employee Benefits
                            textbook was removed (6 chapters from the Employee
                            Benefit Strategy section). However, 2 of these
                            chapters were migrated into a single study note, so
                            effectively only 4 chapters were removed from the
                            syllabus.
                        </li>
                        <li>
                            <b>Removed</b> - Chapter 18 of Life & Health and
                            Annuity Reinsurance (study note from the Risk
                            Adjustment section). This was a fairly long section,
                            with a large amount of testable content.
                        </li>
                        <li>
                            <b>Removed</b> - Value Based Pharmacy: A Canadian
                            Example (study note from the Provider Reimbursement
                            section). A fairly short study note that was not
                            frequently tested.
                        </li>
                        <li>
                            <b>Removed</b> - Chapter 16 the Canadian Handbook of
                            Flexible Benefits (study note from the Employee
                            Benefit Strategy section). A fairly short section
                            overall.
                        </li>
                        <li>
                            <b>Added</b> - 1 new website:{' '}
                            <a
                                href={
                                    'https://www.soa.org/sections/health/health-newsletter/2022/june/hw-2022-06-fann/'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Restoring the Indifference Ideal: If It’s Not
                                Adjusting for “Risk,” It’s Not “Risk Adjustment”
                            </a>
                            . This website is of medium length.
                        </li>
                    </ul>
                    Overall, a lot more was removed from GHRM than was added.
                </div>
                <p>GHVR has no syllabus changes.</p>
            </div>
        </div>
    )
}
